import { CloudAnomalyAlert } from "../../types/alert";
import { AlertCard } from "./AlertCard";
import { Badge } from "../../../../ui/common-ui/badge";
import { Input } from "../../../../ui/common-ui/input";
import { ChevronDown, ChevronUp, User, AlertTriangle, Search, Filter, Brain, BookOpen, Route, Bot } from "lucide-react";
import { useState, useMemo } from "react";
import * as React from "react";

const severityOptions = [
  { value: "Critical", label: "Critical", className: "text-red-400" },
  { value: "High", label: "High", className: "text-orange-400" },
  { value: "Medium", label: "Medium", className: "text-yellow-400" },
];

interface AlertGroupingProps {
  alerts: CloudAnomalyAlert[];
  onViewAlertDetails: (alert: CloudAnomalyAlert) => void;
  externalGroupingMode?: "alertType" | "alert" | "entity";
  externalExpandedGroup?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  severityFilter?: string[];
  onSeverityFilterChange?: (severity: string[]) => void;
  groupingMode: "alertType" | "alert" | "entity";
  onGroupingModeChange: (mode: "alertType" | "alert" | "entity") => void;
  hideEntityGroup?: boolean;
  hideSearch?: boolean;
}

export const AlertGrouping = ({
  alerts,
  onViewAlertDetails,
  externalGroupingMode,
  externalExpandedGroup,
  searchValue = "",
  onSearchChange,
  severityFilter = [],
  onSeverityFilterChange,
  groupingMode,
  onGroupingModeChange,
  hideEntityGroup = false,
  hideSearch = false,
}: AlertGroupingProps) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [showAllAlerts, setShowAllAlerts] = useState(false);

  const groupingOptions = [
    { value: "alert" as const, label: "Alert", icon: AlertTriangle, hidden: false },
    { value: "alertType" as const, label: "Alert Type", icon: Brain, hidden: false },
    { value: "entity" as const, label: "Entity", icon: User, hidden: hideEntityGroup },
  ];

  const handleSeverityChange = (severity: string) => {
    if (!onSeverityFilterChange) return;
    const newSeverity = severityFilter.includes(severity)
      ? severityFilter.filter(s => s !== severity)
      : [...severityFilter, severity];
    onSeverityFilterChange(newSeverity);
  };

  // Handle external grouping mode changes
  React.useEffect(() => {
    if (externalGroupingMode) {
      onGroupingModeChange(externalGroupingMode);
    }
  }, [externalGroupingMode, onGroupingModeChange]);

  // Handle external group expansion
  React.useEffect(() => {
    if (externalExpandedGroup) {
      setExpandedGroups((prev) => {
        const next = new Set(prev);
        next.add(externalExpandedGroup);
        return next;
      });
    }
  }, [externalExpandedGroup]);

  const groupedAlerts = useMemo(() => {
    // Sort by creation date
    const sorted = [...alerts].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    // Group by the selected grouping mode
    const groups = new Map<string, CloudAnomalyAlert[]>();
    sorted.forEach((alert) => {
      let groupKey: string;
      if (groupingMode === "alertType") {
        // Group by BEHAVIOUR, RULE, SEQUENTIAL
        groupKey = alert.alertType;
      } else if (groupingMode === "alert") {
        // Group by title
        groupKey = alert.title;
      } else {
        // Group by entity
        groupKey = alert.entity;
      }

      if (!groups.has(groupKey)) {
        groups.set(groupKey, []);
      }
      groups.get(groupKey)!.push(alert);
    });

    const grouped = Array.from(groups.entries()).map(([title, items]) => ({
      title,
      count: items.length,
      alerts: items,
      severity: items[0].severity,
      hasSequential: items.some(item => item.alertType === "SEQUENTIAL"),
      alertType: items[0].alertType,
      isNonHumanEntity: items[0].isNonHumanEntity,
    }));

    // Sort by severity: Critical > High > Medium
    const severityOrder: Record<string, number> = {
      critical: 0,
      high: 1,
      medium: 2,
    };

    grouped.sort((a, b) => {
      const orderA = severityOrder[a.severity] ?? 999;
      const orderB = severityOrder[b.severity] ?? 999;
      return orderA - orderB;
    });

    return grouped;
  }, [alerts, groupingMode]);

  const toggleGroup = (title: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(title)) {
        next.delete(title);
      } else {
        next.add(title);
      }
      return next;
    });
  };

  const expandAll = () => {
    setExpandedGroups(new Set(groupedAlerts.map((g) => g.title)));
  };

  const collapseAll = () => {
    setExpandedGroups(new Set());
  };

  const getSeverityColor = (severity: string) => {
    const severityLabel = getSeverityLabel(severity);
    if (severityLabel === "Critical") return "bg-red-500/10 border-red-500/30 text-red-400";
    if (severityLabel === "High") return "bg-orange-500/10 border-orange-500/30 text-orange-400";
    if (severityLabel === "Medium") return "bg-yellow-500/10 border-yellow-500/30 text-yellow-400";
    return "bg-blue-500/10 border-blue-500/30 text-blue-400";
  };

  const getSeverityLabel = (severity: string) => {
    const normalized = severity.toLowerCase();
    const map: Record<string, string> = {
      critical: "Critical",
      high: "High",
      medium: "Medium",
    };
    return map[normalized] || (severity.charAt(0).toUpperCase() + severity.slice(1).toLowerCase());
  };

  const getSeverityGradient = () => {
    return "from-card to-card/50";
  };

  const getAlertTypeLabel = (type: string) => {
    const map: Record<string, string> = {
      BEHAVIOUR: "Behaviour",
      RULE: "Rule",
      SEQUENTIAL: "Sequential",
    };
    return map[type] || type;
  };

  const getAlertTypeIcon = (type: string) => {
    if (type === "BEHAVIOUR") return Brain;
    if (type === "SEQUENTIAL") return Route;
    return BookOpen;
  };

  const getAlertTypeBadgeStyle = (type: string) => {
    if (type === "BEHAVIOUR") return "bg-purple-100 border-purple-300 text-purple-700";
    if (type === "SEQUENTIAL") return "bg-indigo-100 border-indigo-300 text-indigo-700";
    return "bg-blue-100 border-blue-300 text-blue-700";
  };

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="cloud-card-surface bg-card border-border/50 overflow-hidden rounded-2xl">
        <div className="flex flex-col md:flex-row md:items-center justify-between p-3 gap-6">
          <div className="flex items-center">
            <div className="cs-tabs !m-0 !w-auto !bg-transparent">
              {groupingOptions
                .filter((option) => !option.hidden)
                .map((option) => {
                  const Icon = option.icon;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        onGroupingModeChange(option.value);
                        setExpandedGroups(new Set());
                      }}
                      className={`cs-tab ${groupingMode === option.value ? "active" : ""}`}
                    >
                      <Icon className="w-4 h-4" />
                      {option.label}
                    </button>
                  );
                })}
            </div>
          </div>

          <div className="flex items-center">
            <div className="cs-tabs !m-0 !w-auto !bg-transparent">
              <button type="button" onClick={expandAll} className="cs-tab">
                Expand All
              </button>
              <button type="button" onClick={collapseAll} className="cs-tab">
                Collapse All
              </button>
              <button
                type="button"
                onClick={() => setShowAllAlerts(!showAllAlerts)}
                className={`cs-tab ${showAllAlerts ? "active" : ""}`}
              >
                {showAllAlerts ? "Group View" : "List View"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Search bar */}
      {!hideSearch && (
        <div className="cloud-card-surface w-full rounded-2xl p-3">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={
                groupingMode === "alertType"
                  ? (hideEntityGroup ? "Search by alert name" : "Search with Alert or Entity")
                  : groupingMode === "alert"
                    ? "Search by alert title"
                    : "Search by entity"
              }
              value={searchValue}
              onChange={(e) => onSearchChange?.(e.target.value)}
              disabled={groupingMode === "alertType"}
              className={`pl-11 pr-4 h-11 bg-white dark:bg-slate-800 border-border/50 shadow-none focus:bg-white dark:focus:bg-slate-800 transition-colors rounded-xl ${groupingMode === "alertType" ? "opacity-50 cursor-not-allowed" : ""
                }`}
            />
          </div>
        </div>
      )}

      {/* Ungrouped view */}
      {showAllAlerts ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 animate-fade-in">
          {groupedAlerts
            .flatMap((group) => group.alerts)
            .map((alert) => (
              <AlertCard
                key={alert.id}
                alert={alert}
                onViewDetails={onViewAlertDetails}
              />
            ))}
        </div>
      ) : (
        /* Grouped view */
        <div className="space-y-6">
          {groupedAlerts.map((group) => {
            const isExpanded = expandedGroups.has(group.title);
            const severityLabel = getSeverityLabel(group.severity);
            const severityColor = getSeverityColor(group.severity);
            const severityGradient = getSeverityGradient();
            const isAlertTypeMode = groupingMode === "alertType";
            const GroupIcon = isAlertTypeMode ? getAlertTypeIcon(group.title) : null;

            const isAlertMode = groupingMode === "alert";
            const AlertTypeIcon = getAlertTypeIcon(group.alertType);

            return (
              <div
                key={group.title}
                className={`border border-border/50 rounded-2xl overflow-hidden bg-gradient-to-br ${severityGradient} backdrop-blur-sm transition-all duration-300 shadow-lg shadow-sky-500/5 hover:border-primary/30 hover:shadow-[0_4px_16px_hsl(var(--primary)/0.06)] hover:-translate-y-px`}
              >
                {/* Group header */}
                <button
                  onClick={() => toggleGroup(group.title)}
                  className="w-full h-16 px-5 flex items-center justify-between hover:bg-muted/20 transition-all duration-200 group"
                >
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="flex-shrink-0">
                      {isExpanded ? (
                        <ChevronUp className="h-5 w-5 text-primary transition-transform duration-200 group-hover:scale-110" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-muted-foreground transition-transform duration-200 group-hover:scale-110" />
                      )}
                    </div>
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {isAlertTypeMode && GroupIcon && (
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <GroupIcon className="h-4 w-4 text-primary" />
                        </div>
                      )}
                      {isAlertMode && (
                        <div className="flex items-center gap-2">
                          <Badge
                            variant="outline"
                            className={`${getAlertTypeBadgeStyle(group.alertType)} flex-shrink-0 border text-xs font-medium gap-1 px-2 py-0.5`}
                          >
                            <AlertTypeIcon className="h-3 w-3" />
                            {getAlertTypeLabel(group.alertType)}
                          </Badge>
                          <Badge
                            variant="outline"
                            className={`${severityColor} flex-shrink-0 border text-xs font-medium px-2 py-0.5`}
                          >
                            {severityLabel}
                          </Badge>
                          <span className="text-foreground text-sm font-semibold truncate">
                            {group.title}
                          </span>
                        </div>
                      )}
                      {isAlertTypeMode && (
                        <span className="text-foreground text-sm font-semibold truncate">
                          {getAlertTypeLabel(group.title)}
                        </span>
                      )}
                      {groupingMode === "entity" && (
                        <div className="flex items-center gap-2">
                          <Badge
                            variant="outline"
                            className={`flex-shrink-0 border text-xs font-medium gap-1 px-2 py-0.5 ${group.isNonHumanEntity
                              ? "bg-slate-100 border-slate-300 text-slate-700"
                              : "bg-blue-100 border-blue-300 text-blue-700"
                              }`}
                          >
                            {group.isNonHumanEntity ? (
                              <>
                                <Bot className="h-3 w-3" />
                                Service Account
                              </>
                            ) : (
                              <>
                                <User className="h-3 w-3" />
                                User
                              </>
                            )}
                          </Badge>
                          <span className="text-foreground text-sm font-semibold truncate">
                            {group.title}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <Badge variant="secondary" className="ml-4 flex-shrink-0 font-medium">
                    {group.count} alert{group.count !== 1 ? "s" : ""}
                  </Badge>
                </button>

                {/* Group content */}
                {isExpanded && (
                  <div className="px-5 pb-5 pt-2 overflow-hidden animate-accordion-down">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                      {group.alerts.map((alert, index) => (
                        <div
                          key={alert.id}
                          className="animate-scale-in"
                          style={{
                            animationDelay: `${index * 50}ms`,
                          }}
                        >
                          <AlertCard
                            alert={alert}
                            onViewDetails={onViewAlertDetails}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
